import { ScaleLinear } from 'd3-scale';
import { Resource } from '@mews-commander/resources';
import { IntervalType } from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { CalendarEvent } from '@mews-commander/data-access';
import {
    CommonResourceUseProps,
    ReservationResourceUse,
    WithTimelineCoordinates,
} from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { NewReservationInfo } from '../newReservation/components/NewReservationPanel/types';
import { TimelineFilterValuesExtended } from './components/TimelineFilters/timelineFilterSchema';
import Model from './components/model';

export interface CursorPosition {
    x: number;
    y: number;
}

export interface Resources {
    leafCount: number;
    maxDepth: number;
    resources: Resource[];
}
export interface AvailabilityWarningProps {
    assignedCategoryId: string | null | undefined;
    selectedCategoryId: string | null | undefined;
    serviceId: string;
    reservationId: string;
    resourceId: string;
}

export enum NavigationDirection {
    Prev = 'Prev',
    Next = 'Next',
}

export interface TimelineBlock {
    horizontalRange: IntervalType;
    verticalRange: IntervalType;
}

export type TimelineModel = ReturnType<typeof Model>;

export interface TimelineCanvas {
    handlePrevFast: () => void;
    handlePrev: () => void;
    handleToday: () => void;
    handleNext: () => void;
    handleNextFast: () => void;

    handleGoToResource: (name: string) => void;
    handleGoToResourceUse: (id: string, isReservation: boolean) => void;
    handleGoToDate: (utc: string) => void;

    handleToggleResourceLegend: () => void;
    clearConfirmedResourceUsePlaceholder: () => void;
}

export interface TimelineOptions {
    target: string;
    isMobile: boolean;
    timeUnit: TimeUnit;
    serviceId: string;
    isTimelineDraggingEnabled?: boolean;
    canManageReservations: boolean;
    resources: Resources;
    isTimelineTouchMoveEnabled: boolean;
    isPercentageDensityValuesEnabled: boolean;
    timelineTouchScreenTest: boolean;
    timezone: string;
    companionExtent: $TSFixMe;
    resourceToHighlight: Resource[];
    selectedDensity: number | string;
    selectedDateTime: string | undefined;
    startOffset: string;
    endOffset: string;
    newReservationEnabled: boolean;
    serviceTimeUnit: TimeUnit;
    maximumAvailableEditableHistoryIntervalInMilliseconds: number;
    confirmedResourceUsePlaceholder: NewReservationInfo | null;
    newReservationPlaceholderLabel: string;
    handleChangeView: (values: TimelineFilterValuesExtended, directFilterUse?: boolean) => void;
    handleSelectResourceUse: <T extends CommonResourceUseProps>(resourceUse: T) => void;
    handleResourceDetail: (resourceId: string) => void;
    handleDeselectResourceUse: () => void;
    handleFetchReservation: (id: string) => void;
    handleFetchBlock: (id: string) => void;
    handleAssignResource: (params: { resourceUseId: string; resourceId: string }) => void;
    refresh: (minX: number, maxX: number, minY: number, maxY: number, model: TimelineModel) => void;
    onViewportChanged: (minX: number, maxX: number, minY: number, maxY: number, model: TimelineModel) => void;
    handleConfirmNewReservation: (newReservationInfo: NewReservationInfo) => void;
    handleCloseNewReservationPanel: () => void;
}

export interface TimelineState {
    ctx: CanvasRenderingContext2D;
    xAxis: ScaleLinear<number, number, unknown>;
    yAxis: ScaleLinear<number, number, unknown>;
    constXAxis: ScaleLinear<number, number, unknown>;
    constYAxis: ScaleLinear<number, number, unknown>;
    canvasWidth: number;
    canvasHeight: number;
    scale: number;
    selectedResourceUse: ReservationResourceUse | null;
    highlightedSpaces: Resource[];
    searchedSpace: Resource | null;
    viewport: { minX: number; maxX: number; minY: number; maxY: number };
    initialOffset: number;
    withResourceLegend: boolean;
    serviceId: string;
    timeUnit: TimeUnit;
    timeUnitPrevious: TimeUnit | null;
    isTimelineDraggingEnabled: boolean;
    filterFloorNumbers: string[];
    filterFeatureIds: string[];
    filterCategoryIds: string[];
    filterUnassignedReservationsEnabled: boolean;
    mouse: CursorPosition | null;
    timezone: string;
    startOffset: string;
    endOffset: string;
    serviceTimeUnit: TimeUnit;
    maximumAvailableEditableHistoryIntervalInMilliseconds: number;
    newReservationPlaceholderLabel: string;
    newReservationEnabled: boolean;
}

export interface Coordinates {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
}

export interface CalendarEventExtended extends CalendarEvent, WithTimelineCoordinates {}
